"use client";

import React, { useRef, useState, useEffect } from "react";

import Image from "next/image";
import CornerChip from "@/components/global/ui/CornerChip";

import { cn } from "@/lib/utils";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { CTACornerButton } from "@/shadcn/ui/corner-button";
import {
  HLSVideoPlayer,
  SanityVideoPlayer,
} from "@/components/ui/SanityVideoPlayer";
import { Dialog, DialogContent } from "@/shadcn/ui/dialog";

import { PageAboutSchema } from "@/sanity/pages/PageAboutTemplate";

import volumeMute from "@/public/icons/Volume--mute--filled.svg";
import volumeUnmute from "@/public/icons/Volume--up--filled.svg";
import maximize from "@/public/icons/Maximize.svg";
import screenEffect from "@/public/about/screen-effect.svg";
import { PageInnerCircleSchema } from "@/sanity/pages/PageInnerCircleTemplate";
import Chip from "../global/ui/Chip";
import Divider from "../global/ui/Divider";
import DividerGreenLine from "../global/ui/DividerGreenLine";
import ChipScrollingBanner from "../global/ui/ChipScrollingBanner";
import GridNoise from "../global/ui/GridNoise";

export default function HeroSection({
  data,
  className,
}: {
  data: PageInnerCircleSchema;
  className?: string;
}) {
  return (
    <section
      className={cn(
        "relative lg:min-h-[122.5rem] flex lg:pb-[10rem]",
        className,
      )}
    >
      <Image
        className="hidden md:block absolute left-0 top-0 !w-full !h-full object-cover"
        src={data?.heroImage}
        quality={100}
        alt="Background image"
      />
      <Image
        className="lg:hidden absolute left-0 top-0 !w-full !h-full object-cover"
        src={data?.heroImageMobile}
        quality={100}
        alt="Background image"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#0A1A1D] w-full h-full" />
      <GridNoise />

      <div className="relative flex flex-col items-center mx-auto px-[2rem] lg:px-0 pt-[16.88rem] lg:pt-[23rem]">
        <Chip
          className="prose-img:!w-[2rem]"
          image={data?.heroChip?.icon}
          text={data?.heroChip?.text}
        />
        {data?.heroTitle && (
          <div className="w-[min(125rem,95%)] richtext richtext-center prose-headings:gradient-title-white prose-headings:text-[8rem] lg:prose-headings:text-[14rem] prose-headings:font-ivy prose-headings:text-transparent prose-p:text-white/60 prose-img:my-0 prose-img:!w-[6.625rem] lg:prose-img:!w-[11.625rem] prose-headings:!mb-0 prose-headings:px-[1rem] prose-headings:pb-[2.75rem] prose-headings:leading-[95%]">
            <PortableText
              value={data.heroTitle}
              components={richTextComponent}
            />
          </div>
        )}
        <DividerGreenLine className="w-[40.75rem] lg:w-[71.5rem] my-[3rem]" />
        {data?.heroText && (
          <div
            className="w-[min(91.75rem,100%)] richtext richtext-center prose-em:font-ivy prose-headings:text-white prose-headings:text-[2.25rem] prose-headings:uppercase prose-headings:font-light
            prose-p:text-white/60 prose-p:text-[2.25rem] prose-p:font-light prose-p:leading-[150%]"
          >
            <PortableText
              value={data.heroText}
              components={richTextComponent}
            />
          </div>
        )}

        {data?.heroCTA && (
          <div className="mt-[6rem] w-[40rem]">
            <CTACornerButton data={data.heroCTA} />
          </div>
        )}
        <span className="mt-[1rem] text-white/60 text-[2.25rem] font-light leading-[150%]">
          {data?.ctaText}
        </span>

        <ChipScrollingBanner
          texts={data.heroChips}
          speed="fast"
          direction="backward"
          className="mt-[14.25rem] lg:mt-[22.87rem]"
        />
      </div>
    </section>
  );
}
